import _ from 'underscore';
import Backbone from 'backbone';
import sortBy from '../helpers/sortBy';

function createLayers(handleLayers, layerDefaults, layersConfig) {
  let layersConf = sortBy(layersConfig);
  let layersLength = layersConf.length,
    alreadyBooked = handleLayers.bookOverlaysSpace(layersLength),
    layers = [];

  layersConf.forEach((layerConf, index) => {
    layerConf.index = layersLength - index + alreadyBooked;
    // We clone the defaults so it's not overwritten by _.extend
    let defaults = layerDefaults ? _.clone(layerDefaults) : {};
    layerConf = _.extend(defaults, layerConf);
    let layer = handleLayers.createLayer(layerConf);
    if (layer.model.get('visibleOnMap')) {
      handleLayers.addToMap(layer);
    }
    layers.push(layer);
  });

  return layers;
}

function updateVisibleFeatures(widget) {
  widget.features = [];
  for (let index in widget.productLayers) {
    if (widget.productLayers.hasOwnProperty(index)) {
      let layer = widget.productLayers[index];
      if (layer.model.get('active') && layer.model.get('visibleOnMap')) {
        widget.features = _.union(layer.features, widget.features);
      }
    }
  }
}

/**
 * Validate the configuration of the product layers
 * @private
 */
function validateProductLayers(productLayers) {
  _.each(productLayers, (layer) => {
    //If markers are updated dinamically we can't fit bounds automatically so we disable it
    if (!layer.params.searchOnce) {
      layer.params.fitBounds = false;
    }
  });
}

/**
 * Class to create and handle layers behaviours
 * @memberof module:Layers
 * @classdesc Create and handle layers on the map.
 * @listens EventBus#FIT_BOUNDS_LAYERS to update the widget's view to the markers displayed in an API Layer
 */
class HandleLayers {

  /**
   * Creates the HandleLayers instance
   * @param {Widget} widget - The needed widget to handle events
   */
  constructor(widget) {
    this.map = widget.map;
    this.bookedSpaces = 0;
    this.layers = [];
    this.layersView = [];
    this.widget = widget;
    this.layersCollection = new Backbone.Collection({});
    this.clusters = {};
    this.minZIndexProductLayers = 1000;
    this.minZIndexLayers = 1;
    this.jumpValueZIndex = 10; //add a Zindex number by 10
    this.zIndexAdded = []; //to know which zIndexs are used

    this.widget.listenTo(this.bus, 'FIT_BOUNDS_LAYERS', () => {
      updateVisibleFeatures(this.widget);
      if (this.widget.features.length > 0) {
        let boundingBox = new google.maps.LatLngBounds();
        this.widget.features.forEach((feature) => {
          let featurePosition = new google.maps.LatLng(feature.geometry.coordinates[1],
            feature.geometry.coordinates[0]);
          boundingBox.extend(featurePosition);
        });
        this.map.fitBounds(boundingBox);
      }
    });
  }

  /**
   * Method to save array space to load each layer
   * @param {Integer} spaces - Number of spaces to save
   * @returns {Integer}
   */
  bookOverlaysSpace(spaces) {
    let startedAt = this.bookedSpaces;
    this.bookedSpaces = this.bookedSpaces + spaces;
    return startedAt;
  }

  /**
   * Method to create a layer
   * @param {object} source - Layer params
   * @param {String} layerId - Layer ID
   * @returns {module:core.layers.BaseLayer}
   */
  createLayer(source, layerId) {
    if (!source.id && layerId) {
      source.id = layerId;
    }

    if (_.indexOf(this.layers, source) === -1 && _.isUndefined(source.layer)) {
      if (_.isUndefined(this.widget.layerClasses[source.type])) {
        throw new Error(source.type + ' layer type doesn\'t exist');
      } else if (source.id) {
        let _model = this.layersCollection.get(source.id);
        if (_model) {
          let layer = this.layersView.filter((source) => {
            return _model === source.model;
          });
          if (_.isEmpty(layer)) { //if the layer view doesn't exist, create a new one.
            return this.createLayerView(source);
          } else { //if the layer view exists, return the prevous one.
            return layer[0];
          }
        } else {
          return this.createLayerView(source);
        }
      } else {
        return this.createLayerView(source);
      }
    } else {
      return source.layer;
    }
  }

  /**
   * Method to add a layer on the map
   * @param {module:Layers.BaseLayer} layer - Layer to be loaded
   */
  addToMap(layer) {
    if (_.indexOf(this.layers, layer) === -1) {
      this.layers.push(layer);
    }
    let index = layer.index = layer.model.get('index') || layer.index || this.bookOverlaysSpace(1) + 1;
    layer.add(index);
  }

  /**
   * Method to get a layer index
   * @param {Object} idLayer - id layer
   * @returns {number} layerIndex
   */
  getLayerIndex(idLayer) {
    let layerIndex = _.findIndex(this.layers, (layer) => {
      return layer.id === idLayer;
    });

    return layerIndex;
  }

  /**
   * Method to remove a layer from the map
   * @param {module:Layers.BaseLayer} layer - Layer to be removed
   */
  deleteLayer(layer) {
    let index = _.indexOf(this.layers, layer);
    if (index > -1) {
      layer.remove();
    }
  }

  /**
   * Method to remove a layer by id from the map
   * @param {Object} idLayer - Layer id to be removed
   */
  deleteLayerById(idLayer) {
    let layerToRemove = this.getLayerIndex(idLayer);
    if (layerToRemove > -1) {
      this.layers[layerToRemove].remove();
      this.layers.splice(layerToRemove, 1);
    }
  }

  /**
   * Method to get the model from a layer
   * @param {String} layerId - Layer id to get its model
   * @returns {object}
   */
  getLayerModel(layerId) {
    return this.layersCollection.get(layerId);
  }

  _layerVisibilityFixer(source) {
    let layersToFix = this.layers.filter((src) => {
      return (src.layer === source.layer) && (src !== source) && source.model.get('active');
    });
    _.each(layersToFix, (src) => {
      this.map.overlayMapTypes.setAt(src.index, src.layer);
    });
  }

  /**
   * Method to create a layer view (layer without model)
   * @param {Object} source - Layer parameters
   * @returns {module:Layers.BaseLayer}
   */
  createLayerView(source) {
    let layerView = new this.widget.layerClasses[source.type]({ source: source, widget: this.widget });
    this.layersView.push(layerView);
    return layerView;
  }

  /**
   * Method to remove a layer view (layer without model) from the map
   * @param {String} id - Layer id to be removed
   */
  deleteLayerView(id) {
    let layer, index;
    for (let i = 0; i < this.layersView.length; i++) {
      if (this.layersView[i].id === id) {
        layer = this.layersView[i];
        index = i;
        break;
      }
    }
    if (layer !== null && index > -1) {
      this.layersView.splice(index, 1);
      this.deleteLayer(layer);
    }
  }

  /**
   * Method to get a layer view (layer without model) by id
   * @param {String} id - Layer id to be returned
   * @returns {module:Layers.BaseLayer | null}
   */
  getLayerView(id) {
    let layerView = _.find(this.layersView, (layer) => {
      return layer.id === id;
    });

    return _.isUndefined(layerView) ? null : layerView;
  }

  /**
   * Order the products layers by ZIndex
   * @param {module:Layers.BaseLayer[]} layers - Array of productLayers
   */
  setOrderProductLayers(layers) {
    let minZIndex = this.minZIndexProductLayers; //Product layers have a zIndex higher than others layers
    let maxZindex = this._getMaxZIndex(layers) * this.jumpValueZIndex;
    this._orderLayers(layers, minZIndex, maxZindex);
  }

  /**
   * Order the secondary layers (Google Places, etc) by ZIndex
   * @param {module:Layers.BaseLayer[]} layers - Array of layers
   */
  setOrderSecondaryLayers(layers) {
    let minZIndex = this.minZIndexLayers;
    let maxZindex = this._getMaxZIndex(layers) * this.jumpValueZIndex;
    this._orderLayers(layers, minZIndex, maxZindex);
  }

  /**
   * Order the  layers by ZIndex
   * @private
   * @param {module:Layers.BaseLayer[]} layers - Array of layers
   * @param {Number} minZIndex - The minZIndex for this layers
   * @param {Number} maxZindex - The minimum zIndex for layers without zIndex by config
   */
  _orderLayers(layers, minZIndex, maxZindex) {
    _.each(layers, (layer) => {
      //We increase the range of the zIndex for order others layers with the same zIndex
      let zIndex = this._getZIndex(layer) * this.jumpValueZIndex;
      //We use the same zIndex if it wasn't added before by other layer
      if (zIndex && _.indexOf(this.zIndexAdded, zIndex) === -1) {
        layer.markersZIndex = zIndex;
        //If the zIndex exists, we increment one unit after an unused zIndex
      } else if (zIndex && _.indexOf(this.zIndexAdded, zIndex) > -1) {
        let foundSlot = false;
        //The condition i < zIndex + 30 is for to avoid a infinite loop
        //We try to find the zIndex 30 times maximum
        for (let i = zIndex + 1; !foundSlot && i < zIndex + 30; i++) {
          if (_.indexOf(this.zIndexAdded, i) === -1) {
            layer.markersZIndex = i;
            foundSlot = true;
          }
        }
        //If after 30 units incremented we dont find a zIndex unused, we use the first zIndex
        if (!foundSlot) {
          layer.markersZIndex = zIndex;
        }
      } else {
        //If the layer doesnt have a zIndex by config, we increment the maximum zIndex by config to add one
        let foundSlot = false;
        //The condition i < zIndex + 30 is for to avoid a infinite loop
        //We try to find the zIndex 30 times maximum
        for (let i = maxZindex + 1; !foundSlot && i < maxZindex + 30; i++) {
          if (_.indexOf(this.zIndexAdded, i) === -1) {
            layer.markersZIndex = i;
            foundSlot = true;
          }
        }
        if (!foundSlot) {
          layer.markersZIndex = maxZindex;
        }
      }
      this.zIndexAdded.push(layer.markersZIndex);
    });
  }
  /**
   * Get the zIndex added by config
   * @private
   * @param {module:Layers.BaseLayer} layer - layer
   * @return the zIndex by config or null if it doesn't exist
   */
  _getZIndex(layer) {
    if (!_.isUndefined(layer.markersZIndex) && !_.isNull(layer.markersZIndex)) {
      return layer.markersZIndex;
    } else {
      return null;
    }
  }
  /**
   * Get the zIndex maximum by config in order to find zIndex unused after this number
   * @private
   * @param {module:Layers.BaseLayer[]} layer - array of layers added by config
   * @return the zIndex maximum by config
   */
  _getMaxZIndex(layers) {
    let max = 1;
    _.each(layers, (layer) => {
      if (!_.isUndefined(layer.markersZIndex) && !_.isNull(layer.markersZIndex) && max < layer.markersZIndex) {
        max = layer.markersZIndex;
      }
    });
    return max;
  }

  /**
   * @typedef {object} CreateConfigLayersResult
   * @property {core.layers.BaseLayer[]} productLayers The product layers created.
   * @property {core.layers.BaseLayer[]} layers The non-product layers created   * 
   * @memberof core.widget.widgetHelpers.HandleLayers
   */

  /**
   * Creates layers from their configuration.
   * @param {object} layerDefaults
   * @param {object} productLayersCfg 
   * @param {object} layersCfg 
   * @returns {core.widget.widgetHelpers.CreateConfigLayersResult}
   */
  createConfigLayers(layerDefaults, productLayersCfg, layersCfg) {
    //Creation of productLayers
    let productLayers = [],
      layers = [];
    if (_.isObject(productLayersCfg)) {
      validateProductLayers(productLayersCfg);
      productLayers = createLayers(this, layerDefaults, productLayersCfg);
      this.setOrderProductLayers(productLayers);
    }
    //Creation of layers gPlaces, carto or WMS
    if (_.isObject(layers)) {
      layers = createLayers(this, layerDefaults, layersCfg);
      this.setOrderSecondaryLayers(layers);
    }

    return {
      productLayers: productLayers,
      layers: layers
    };
  }
}

export default HandleLayers;



// WEBPACK FOOTER //
// ./lmm-widget/src/layers/HandleLayers.js